// OfficerQueue widget — display-only (H.3.4a).
//
// Renders the queue-inspector tool result: a `:::widget[officer-queue]
// {payload="<base64 JSON>"}:::` directive. The payload is the projector's
// GET /admin/officer/{officerId}/queue?expand=true response
// ({ pending, in_progress, completed_today, violations?, officerId? }).
// Decode → render a Card with the counts + the pending list. No interaction.

import { Card } from '@rmint/ui';

interface Props {
  payload?: string;
}

interface QueueData {
  officerId?: string;
  pending?: string[];
  in_progress?: string[];
  completed_today?: string[];
  completed?: string[];
  violations?: unknown[];
}

function decodePayload(payload?: string): QueueData | null {
  if (!payload) return null;
  try {
    return JSON.parse(atob(payload)) as QueueData;
  } catch {
    return null;
  }
}

function shortName(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] ?? path;
}

export function OfficerQueue({ payload }: Props) {
  const data = decodePayload(payload);
  if (!data) {
    return (
      <div
        data-testid="officer-queue"
        className="my-2 rounded border border-destructive/30 px-3 py-2 text-sm text-destructive"
      >
        Officer queue: no payload.
      </div>
    );
  }

  const pending = data.pending ?? [];
  const inProgress = data.in_progress ?? [];
  const completed = data.completed_today ?? data.completed ?? [];
  const violations = data.violations ?? [];

  return (
    <Card data-testid="officer-queue" className="my-2">
      <div className="text-sm font-semibold text-foreground mb-2">
        Officer queue{data.officerId ? ` — ${data.officerId}` : ''}
      </div>
      <div className="grid grid-cols-4 gap-2 text-center text-xs mb-3">
        <div>
          <div className="text-lg font-bold text-foreground">{pending.length}</div>
          <div className="text-foreground-mute">Pending</div>
        </div>
        <div>
          <div className="text-lg font-bold text-foreground">{inProgress.length}</div>
          <div className="text-foreground-mute">In progress</div>
        </div>
        <div>
          <div className="text-lg font-bold text-foreground">{completed.length}</div>
          <div className="text-foreground-mute">Completed</div>
        </div>
        <div>
          <div className="text-lg font-bold text-foreground">{violations.length}</div>
          <div className="text-foreground-mute">Violations</div>
        </div>
      </div>
      {pending.length > 0 ? (
        <ul className="space-y-0.5 text-xs text-foreground/80">
          {pending.map((p) => (
            <li key={p} className="font-mono truncate" title={p}>
              {shortName(p)}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-foreground-mute">No pending reviews.</div>
      )}
    </Card>
  );
}
